import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowRight, Heart, Users, Leaf, Award, Calendar, ChevronRight, ChevronLeft, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { PageSEO } from '../components/PageSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero Slider Data
const heroSlides = [
  {
    image: '/images/hero/hero1.webp',
    title: 'Arılar İçin,\nDoğa İçin,\nGelecek İçin.',
    subtitle: 'Sürdürülebilir arıcılık uygulamaları ile doğayı koruyoruz, kadın üreticileri destekliyoruz.',
    cta: { text: 'Üye Ol', link: '/katil' }
  },
  {
    image: '/images/hero/hero2.webp',
    title: 'Doğal Bal,\nGerçek Lezzet',
    subtitle: 'KOSAD üreticilerinden doğrudan sofranıza gelen %100 doğal arı ürünleri.',
    cta: { text: 'Markete Git', link: '/market' }
  },
  {
    image: '/images/hero/hero3.webp',
    title: 'Kadın Üreticileri\nDestekliyoruz',
    subtitle: '25+ kadın girişimci ile birlikte sürdürülebilir arıcılık ekosistemi oluşturuyoruz.',
    cta: { text: 'Hikayelerini Keşfet', link: '/market' }
  },
  {
    image: '/images/hero/hero4.webp',
    title: 'Kocaeli\'den\nDünyaya Değer',
    subtitle: 'Eğitim, dayanışma ve doğa koruma ile sürdürülebilir bir gelecek inşa ediyoruz.',
    cta: { text: 'Projelerimiz', link: '/calismalarimiz' }
  }
];

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const HomePage = () => {
  const [projects, setProjects] = useState([]);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    fetchData();
    // Auto-slide
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, newsRes, eventsRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects`),
        axios.get(`${API_URL}/api/news`),
        axios.get(`${API_URL}/api/events`),
        axios.get(`${API_URL}/api/products`)
      ]);
      setProjects(projectsRes.data.slice(0, 3));
      setNews(newsRes.data.slice(0, 3));
      setEvents(eventsRes.data.slice(0, 2));
      setProducts(productsRes.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} sepete eklendi!`);
  };

  const stats = [
    { icon: Users, value: 500, suffix: '+', label: 'Üye Arıcı' },
    { icon: Heart, value: 15000, suffix: '+', label: 'Kovan' },
    { icon: Leaf, value: 50, suffix: '+', label: 'Eğitim Programı' },
    { icon: Award, value: 25, suffix: '+', label: 'Kadın Üretici' }
  ];

  const whatWeDo = [
    {
      icon: '🐝',
      title: 'Sürdürülebilir Arıcılık',
      description: 'Modern ve çevre dostu arıcılık tekniklerini yaygınlaştırıyoruz.'
    },
    {
      icon: '📚',
      title: 'Eğitim & Bilinçlendirme',
      description: 'Arıcılık eğitimleri ve ekosistem farkındalığı programları düzenliyoruz.'
    },
    {
      icon: '👩‍🌾',
      title: 'Kadın Üreticiler',
      description: 'Kadın arıcıların ekonomik güçlenmesini destekliyoruz.'
    },
    {
      icon: '🌍',
      title: 'Ekosistem Koruma',
      description: 'Arılar ve polinatörler için yaşam alanları oluşturuyoruz.'
    }
  ];

  return (
    <div data-testid="home-page" className="grain-overlay relative">
      <PageSEO 
        title="Ana Sayfa"
        description="KOSAD - Kocaeli Sürdürülebilir Arıcılık ve Dayanışma Derneği. Doğal bal, propolis ve arı ürünleri. Kadın üreticileri destekleyen sürdürülebilir arıcılık ekosistemi."
        path="/"
      />
      {/* Hero Slider Section */}
      <section className="relative h-[90vh] overflow-hidden" data-testid="hero-section">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0"
          >
            <img 
              src={heroSlides[currentSlide].image} 
              alt="Hero" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A3625]/90 via-[#1A3625]/60 to-[#1A3625]/20" />
          </motion.div>
        </AnimatePresence>
        
        <div className="relative container-custom h-full flex items-center">
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentSlide}
              className="max-w-2xl"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.span 
                className="label-uppercase text-[#D4AF37] mb-4 block"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Kocaeli Sürdürülebilir Arıcılık ve Dayanışma Derneği
              </motion.span>
              
              <motion.h1 
                className="text-3xl sm:text-4xl lg:text-5xl text-white font-semibold mb-6 leading-tight whitespace-pre-line"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {heroSlides[currentSlide].title.split('\n').map((line, i) => (
                  <span key={i}>
                    {i === 2 ? <span className="text-[#D4AF37]">{line}</span> : line}
                    {i < 2 && <br />}
                  </span>
                ))}
              </motion.h1>
              
              <motion.p 
                className="text-base text-white/80 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {heroSlides[currentSlide].subtitle}
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Link to={heroSlides[currentSlide].cta.link} className="btn-gold text-center" data-testid="hero-cta-main">
                  {heroSlides[currentSlide].cta.text}
                </Link>
                <Link to="/calismalarimiz" className="btn-secondary bg-white/10 border-white text-white hover:bg-white/20 text-center">
                  Projelerimizi Keşfet
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          {/* Slider Controls */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-4">
            <button 
              onClick={prevSlide}
              className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
              data-testid="hero-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <div className="flex gap-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                  data-testid={`hero-dot-${index}`}
                />
              ))}
            </div>
            
            <button 
              onClick={nextSlide}
              className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
              data-testid="hero-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* What KOSAD Does */}
      <section className="section-padding bg-white relative" data-testid="what-we-do-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Ne Yapıyoruz</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1A3625]">
              KOSAD'ın Çalışma Alanları
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatWeDo.map((item, index) => (
              <motion.div
                key={index}
                className="card-premium text-center group"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`what-we-do-card-${index}`}
              >
                <span className="text-4xl mb-4 block group-hover:scale-110 transition-transform duration-300">{item.icon}</span>
                <h3 className="text-lg font-medium text-[#1A3625] mb-2">{item.title}</h3>
                <p className="text-base text-[#5C5C5C]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Bees Matter - with glassmorphism */}
      <section className="section-padding relative overflow-hidden" data-testid="why-bees-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A3625]/5 to-[#D4AF37]/5" />
        <div className="container-custom relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">Neden Önemli</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1A3625] mb-6">
                Arılar Olmadan Dünya Olmaz
              </h2>
              <p className="text-base text-[#5C5C5C] mb-4 leading-relaxed">
                Dünya üzerindeki bitkisel gıdaların %75'i arılar ve diğer tozlaştırıcılar sayesinde üretilmektedir. 
                Arı popülasyonundaki azalma, gıda güvenliğimizi ve ekosistem dengesini tehdit etmektedir.
              </p>
              <p className="text-base text-[#5C5C5C] mb-8 leading-relaxed">
                KOSAD olarak, sürdürülebilir arıcılık uygulamalarını yaygınlaştırarak hem arıların 
                korunmasına hem de yerel üreticilerin güçlenmesine katkı sağlıyoruz.
              </p>
              <Link to="/egitim" className="btn-primary inline-flex items-center gap-2" data-testid="learn-more-btn">
                Daha Fazla Bilgi
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="image-hover rounded-3xl shadow-2xl overflow-hidden">
                <img 
                  src="/images/hikaye-arici.jpg"
                  alt="Doğa"
                  className="w-full h-auto"
                />
              </div>
              
              {/* Floating stat card */}
              <motion.div 
                className="absolute -bottom-8 -left-8 card-glass p-6 glow-gold"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <p className="text-4xl font-bold text-[#1A3625]">%75</p>
                <p className="text-base text-[#5C5C5C]">Bitkisel gıda<br />tozlaşma ile üretilir</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="section-padding bg-[#1A3625] relative overflow-hidden" data-testid="projects-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">Projelerimiz</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl text-white">
                Aktif Çalışmalarımız
              </h2>
            </motion.div>
            <Link 
              to="/calismalarimiz" 
              className="mt-4 md:mt-0 text-[#D4AF37] font-medium flex items-center gap-2 hover:gap-3 transition-all"
              data-testid="view-all-projects"
            >
              Tümünü Gör
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                className="bg-white rounded-3xl overflow-hidden group"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -8 }}
                data-testid={`project-card-${project.id}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 ${
                    project.status === 'active' ? 'bg-[#1A3625]/10 text-[#1A3625]' : 
                    project.status === 'planned' ? 'bg-[#D4AF37]/20 text-[#8C7A6B]' :
                    'bg-[#D4AF37] text-white'
                  }`}>
                    {project.status === 'active' ? 'Aktif' : 
                     project.status === 'planned' ? 'Planlanan' : 'Tamamlandı'}
                  </span>
                  <h3 className="text-lg font-medium text-[#1A3625] mb-2">{project.title}</h3>
                  <p className="text-[#5C5C5C] text-base mb-4 line-clamp-2">{project.description}</p>
                  <Link 
                    to={`/calismalarimiz#${project.id}`}
                    className="text-[#1A3625] font-medium text-base flex items-center gap-1 hover:gap-2 transition-all"
                  >
                    Detaylı Bilgi
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats with animated counters */}
      <section className="section-padding relative overflow-hidden" data-testid="stats-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9F8F6] to-white" />
        <div className="container-custom relative">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Etkimiz</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1A3625]">
              Rakamlarla KOSAD
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`stat-${index}`}
              >
                <motion.div 
                  className="w-20 h-20 bg-gradient-to-br from-[#1A3625] to-[#2d5a3f] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <stat.icon className="w-10 h-10 text-white" />
                </motion.div>
                <p className="text-3xl md:text-4xl font-semibold text-[#1A3625] mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-[#5C5C5C]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products with hover quick-add */}
      <section className="section-padding bg-white" data-testid="products-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">Ekosistem Marketi</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1A3625]">
                Doğadan Sofranıza
              </h2>
            </motion.div>
            <Link 
              to="/market" 
              className="mt-4 md:mt-0 text-[#1A3625] font-medium flex items-center gap-2 hover:gap-3 transition-all"
              data-testid="view-all-products"
            >
              Tüm Ürünler
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                className="card-premium group relative product-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`product-card-${product.id}`}
              >
                <Link to={`/market/urun/${product.id}`}>
                  <div className="aspect-square rounded-2xl overflow-hidden mb-4 relative">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                    />
                    {/* Quick add overlay */}
                    <div className="product-card-overlay absolute inset-0 flex items-end justify-center pb-4">
                      <motion.button
                        onClick={(e) => handleAddToCart(product, e)}
                        className="bg-white text-[#1A3625] px-6 py-3 rounded-full font-medium flex items-center gap-2 hover:bg-[#D4AF37] hover:text-white transition-colors"
                        initial={{ y: 20, opacity: 0 }}
                        whileHover={{ scale: 1.05 }}
                        data-testid={`quick-add-${product.id}`}
                      >
                        <ShoppingBag className="w-4 h-4" />
                        Sepete Ekle
                      </motion.button>
                    </div>
                  </div>
                </Link>
                <Link to={`/market/urun/${product.id}`}>
                  <h3 className="text-lg font-medium text-[#1A3625] mb-1 group-hover:text-[#D4AF37] transition-colors">
                    {product.name}
                  </h3>
                </Link>
                <p className="text-base text-[#5C5C5C] mb-3">{product.unit}</p>
                <p className="text-xl font-semibold text-[#D4AF37]">₺{product.price}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="section-padding bg-gradient-to-b from-[#F9F8F6] to-white" data-testid="events-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Etkinlikler</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1A3625]">
              Yaklaşan Etkinlikler
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                className="card-glass flex gap-6"
                initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                data-testid={`event-card-${event.id}`}
              >
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#1A3625] to-[#2d5a3f] rounded-2xl flex flex-col items-center justify-center text-white shadow-lg">
                  <Calendar className="w-6 h-6 mb-1" />
                  <span className="text-sm">{new Date(event.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#1A3625] mb-1">{event.title}</h3>
                  <p className="text-base text-[#5C5C5C] mb-2">{event.description}</p>
                  <p className="text-base text-[#8C7A6B]">{event.location} • {event.time}</p>
                  {event.is_free && (
                    <span className="inline-block mt-2 text-sm bg-[#D4AF37]/20 text-[#8C7A6B] px-2 py-1 rounded-full">
                      Ücretsiz
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* News */}
      <section className="section-padding bg-white" data-testid="news-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">Haberler</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1A3625]">
                Son Gelişmeler
              </h2>
            </motion.div>
            <Link 
              to="/haberler" 
              className="mt-4 md:mt-0 text-[#1A3625] font-medium flex items-center gap-2 hover:gap-3 transition-all"
              data-testid="view-all-news"
            >
              Tüm Haberler
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {news.map((item, index) => (
              <motion.div
                key={item.id}
                className="card-premium group"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`news-card-${item.id}`}
              >
                <div className="image-hover mb-4">
                  <img 
                    src={item.image}
                    alt={item.title}
                    className="w-full aspect-video object-cover"
                  />
                </div>
                <span className="text-base text-[#8C7A6B]">
                  {new Date(item.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <h3 className="text-lg font-medium text-[#1A3625] mt-2 mb-2 group-hover:text-[#D4AF37] transition-colors">{item.title}</h3>
                <p className="text-base text-[#5C5C5C] line-clamp-2">{item.summary}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-[#D4AF37] to-[#c9a432] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1A3625] mb-6">
              Değişimin Parçası Olun
            </h2>
            <p className="text-base text-[#1A3625]/80 mb-8 max-w-2xl mx-auto">
              Arıların ve doğanın korunması için birlikte çalışalım. 
              Üye olun, gönüllü olun veya projelerimizi destekleyin.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/katil" className="btn-primary" data-testid="cta-join-btn">
                Bize Katılın
              </Link>
              <Link to="/market" className="btn-secondary border-[#1A3625]" data-testid="cta-market-btn">
                Marketi Keşfet
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
